import {ApolloQueryResult} from 'apollo-client';
import _ from 'lodash';

import {apolloClientNoCache} from '../../../apolloClient';
import {RunsDataQuery} from '../../../containers/RunsDataLoader';
import {
  BucketedRunsDeltaQueryDocument,
  BucketedRunsDeltaQueryInternalIdDocument,
  BucketedRunsDeltaQueryInternalIdQuery,
  BucketedRunsDeltaQueryInternalIdQueryVariables,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  BucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables,
  BucketedRunsDeltaQueryQuery,
  BucketedRunsDeltaQueryQueryVariables,
  BucketedRunsDeltaQueryWithoutRunInfoDocument,
  BucketedRunsDeltaQueryWithoutRunInfoQuery,
  BucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
  GroupedBucketedRunsDeltaQueryDocument,
  GroupedBucketedRunsDeltaQueryInternalIdDocument,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables,
  GroupedBucketedRunsDeltaQueryQuery,
  GroupedBucketedRunsDeltaQueryQueryVariables,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
  GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {fromJson} from '../../../util/runs';
import {buildTimePoint} from '../componentOutliers/buildPoints';
import {HistoryRecord} from '../componentOutliers/types';
import {BucketedLine} from '../types';
import {SingleQuery} from './../../../util/queryTypes';
import {BucketedQueryType, QueryVars, queryVars} from './queryVars';

type BucketedQueryResult = ApolloQueryResult<BucketedRunsDeltaQueryQuery>;
type BucketedQueryPromise = Promise<BucketedQueryResult>;

type BucketedQueryRuns = NonNullable<
  NonNullable<BucketedQueryResult['data']['project']>['runs']
>;
type BucketedQueryRunsDeltas = NonNullable<
  BucketedQueryRuns['deltas']
>['delta'];
type BucketedQueryRunsDelta = BucketedQueryRunsDeltas[number];

type GroupedBucketedQueryPromise = Promise<
  ApolloQueryResult<GroupedBucketedRunsDeltaQueryQuery>
>;
type GroupedBucketedQueryResult =
  ApolloQueryResult<GroupedBucketedRunsDeltaQueryQuery>;
type GroupedBucketedQueryRuns = NonNullable<
  NonNullable<GroupedBucketedQueryResult['data']['project']>['groupedRuns']
>;
type GroupedBucketedQueryRunsDeltas = NonNullable<
  GroupedBucketedQueryRuns['deltas']
>['delta'];
type GroupedBucketedQueryRunWithGroupedRunConnection = Extract<
  GroupedBucketedQueryRuns,
  {
    __typename: 'GroupedRunConnection';
  }
>;
interface BaseQueryBehavior<TRes, TRuns> {
  query: (
    vars: QueryVars,
    currentRuns: string[],
    lastUpdated: string,
    withoutRunInfo: boolean
  ) => Promise<TRes>;
  unpack: (result: TRes) => TRuns | null | undefined;
  postProcess: (runs: TRuns | null | undefined) => void;
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: TRuns | null | undefined;
  }) => Record<string, BucketedLine>;
}

type GroupedQueryBehaviorType = BaseQueryBehavior<
  GroupedBucketedQueryResult,
  GroupedBucketedQueryRuns
>;

type BucketedQueryBehaviorType = BaseQueryBehavior<
  BucketedQueryResult,
  BucketedQueryRuns
>;

function doBucketedApiQuery(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  withoutRunInfo: boolean
): BucketedQueryPromise {
  if (withoutRunInfo) {
    return apolloClientNoCache.query<
      BucketedRunsDeltaQueryWithoutRunInfoQuery,
      BucketedRunsDeltaQueryWithoutRunInfoQueryVariables
    >({
      context: propagateErrorsContext(),
      query: BucketedRunsDeltaQueryWithoutRunInfoDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  } else {
    return apolloClientNoCache.query<
      BucketedRunsDeltaQueryQuery,
      BucketedRunsDeltaQueryQueryVariables
    >({
      context: propagateErrorsContext(),
      query: BucketedRunsDeltaQueryDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  }
}
function doBucketedApiQueryByInternalId(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string,
  withoutRunInfo: boolean
): BucketedQueryPromise {
  if (withoutRunInfo) {
    return apolloClientNoCache.query<
      BucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
      BucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
    >({
      context: propagateErrorsContext(),
      query: BucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  }
  return apolloClientNoCache.query<
    BucketedRunsDeltaQueryInternalIdQuery,
    BucketedRunsDeltaQueryInternalIdQueryVariables
  >({
    context: propagateErrorsContext(),
    query: BucketedRunsDeltaQueryInternalIdDocument,
    fetchPolicy: 'no-cache',
    variables: {
      ...vars,
      currentRuns,
      lastUpdated,
    },
  });
}

function applyDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
}): Record<string, BucketedLine> {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return currentRunsById;
  } else {
    // new diffOps or deleteOps means we need to refresh the reference for `runsById`
    currentRunsById = Object.assign({}, currentRunsById);
    deleteOps.forEach(rId => {
      delete currentRunsById[rId];
    });
  }

  delta.forEach(runDelta => {
    if (!runDelta.run) {
      console.error('Bucketed Delta Op returned without valid run');
      return;
    }

    try {
      const parsedRun = fromJson(runDelta.run);
      if (parsedRun == null) {
        console.error('Unable to parse run from server in outliers reducer');
        return;
      }

      // Update the entire record for any diffOp
      currentRunsById[runDelta.run.id] = {
        ...parsedRun,
        history: runDelta.run.bucketedHistory,
      };
    } catch (e) {
      console.error('Error parsing run data in outliers reducer', e);
    }
  });
  return currentRunsById;
}

function postProcessRuns(runs: BucketedQueryRuns | null | undefined) {
  // post process data - we need to calculate absolute runtime if it might be used
  // _absolute_runtime is a derived metric that is calculated off the run's start time
  try {
    runs?.deltas?.delta?.forEach((runDelta: BucketedQueryRunsDelta) => {
      const createdAt = runDelta?.run?.createdAt ?? '';
      const startTimeInMs = new Date(createdAt).getTime();
      runDelta?.run?.bucketedHistory?.forEach((bucket: HistoryRecord[]) => {
        if (bucket.length > 0) {
          const firstPoint = bucket[0];

          if (
            '_runtimeLast' in firstPoint &&
            '_timestampLast' in firstPoint &&
            '_stepLast' in firstPoint
          ) {
            // this represents a bucket that we might need _absolute_runtime for, so we need to calculate it
            // when the data comes into the Full Fidelity Context
            bucket.forEach((point: HistoryRecord) => {
              buildTimePoint(point, {
                value: startTimeInMs,
                unit: 'milliseconds',
              });
            });
          }
        }
      });
    });
  } catch (e) {
    console.error(
      'Error creating _absolute_runtime metrics in bucketed query manager',
      e
    );
  }
}

function postProcessGroupedRuns(
  runs: GroupedBucketedQueryRunWithGroupedRunConnection | null | undefined
) {
  try {
    runs?.deltas?.delta?.forEach(runDelta => {
      runDelta?.groupedRun?.bucketedHistory?.forEach(
        (bucket: HistoryRecord[]) => {
          if (bucket.length > 0) {
            const firstPoint = bucket[0];

            if (
              '_runtimeLast' in firstPoint &&
              '_timestampLast' in firstPoint &&
              '_stepLast' in firstPoint
            ) {
              // this represents a bucket that we might need _absolute_runtime for, so we need to calculate it
              // when the data comes into the Full Fidelity Context
              bucket.forEach((point: HistoryRecord) => {
                buildTimePoint(point, {
                  value: NaN, // TODO: add createdAt to GroupedRun
                  unit: 'milliseconds',
                });
              });
            }
          }
        }
      );
    });
  } catch (e) {
    console.error(
      'Error creating _absolute_runtime metrics in bucketed query manager',
      e
    );
  }
}

const BucketedQueryBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQuery,
  unpack: (result: BucketedQueryResult) => result?.data?.project?.runs,
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

const BucketedQueryWithInternalIdBehavior: BucketedQueryBehaviorType = {
  query: doBucketedApiQueryByInternalId,
  unpack: (result: BucketedQueryResult) => result?.data?.project?.runs,
  postProcess: postProcessRuns,
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: BucketedQueryRuns | null | undefined;
  }) => {
    const deltas = vars.runs?.deltas?.delta ?? [];
    const order = vars.runs?.deltas?.order ?? [];
    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order,
      delta: deltas,
    });
  },
};

function isAggregatedRuns(
  runs: GroupedBucketedQueryRuns | null | undefined
): runs is GroupedBucketedQueryRunWithGroupedRunConnection {
  return runs?.__typename === 'GroupedRunConnection';
}

const GroupedBucketedQueryBehavior: GroupedQueryBehaviorType = {
  query: doGroupedBucketedApiQuery,
  unpack: (result: GroupedBucketedQueryResult) =>
    result?.data?.project?.groupedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
      });
    }

    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order: runs?.deltas?.order ?? [],
      delta: runs?.deltas?.delta ?? [],
    });
  },
};

const GroupedBucketedQueryWithInternalIdBehavior: GroupedQueryBehaviorType = {
  query: doGroupedBucketedApiQueryByInternalId,
  unpack: (result: GroupedBucketedQueryResult) =>
    result?.data?.project?.groupedRuns,
  postProcess: (runs: GroupedBucketedQueryRuns | null | undefined) => {
    if (runs?.__typename === 'GroupedRunConnection') {
      return postProcessGroupedRuns(runs);
    }
    return postProcessRuns(runs);
  },
  applyDeltaFn: (vars: {
    currentRunsById: Record<string, BucketedLine>;
    runs: GroupedBucketedQueryRuns | null | undefined;
  }) => {
    const runs = vars.runs;
    if (isAggregatedRuns(runs)) {
      return applyGroupedDelta({
        currentRunsById: vars.currentRunsById,
        order: runs.deltas?.order ?? [],
        delta: runs.deltas?.delta ?? [],
      });
    }

    return applyDelta({
      currentRunsById: vars.currentRunsById,
      order: runs?.deltas?.order ?? [],
      delta: runs?.deltas?.delta ?? [],
    });
  },
};

async function executeQueryBehavior<
  TRes,
  TRuns extends {__typename: string; totalCount?: number}
>(
  behavior: BaseQueryBehavior<TRes, TRuns>,
  vars: QueryVars,
  runIds: string[],
  lastUpdated: string,
  isShadowQuery: boolean,
  currentRunsById: Record<string, BucketedLine>,
  withoutRunInfo: boolean
) {
  const result = await behavior.query(
    {
      ...vars,
      limit: isShadowQuery ? randomShadowLimit() : vars.limit,
    },
    runIds,
    lastUpdated,
    withoutRunInfo
  );

  if (isShadowQuery) {
    return {
      runsById: {},
      isAggregated: false,
      totalRuns: undefined,
    };
  }

  const runs = behavior.unpack(result);
  behavior.postProcess(runs);
  const runsById = behavior.applyDeltaFn({
    currentRunsById,
    runs,
  });
  return {
    runsById,
    isAggregated: runs?.__typename === 'GroupedRunConnection',
    totalRuns: runs?.totalCount,
  };
}

export async function bucketedQuery({
  currentRunsById,
  expressions,
  isShadowQuery,
  nBuckets,
  queryType,
  runsDataQuery,
  singleQuery,
  withoutRunInfo,
}: {
  currentRunsById: Record<string, BucketedLine>;
  expressions: string[] | undefined;
  isShadowQuery: boolean;
  nBuckets: number;
  queryType: BucketedQueryType;
  runsDataQuery: RunsDataQuery;
  singleQuery: SingleQuery;
  withoutRunInfo: boolean;
}) {
  const runIds = Object.keys(currentRunsById);
  const lastUpdated =
    _.max(Object.values(currentRunsById).map(l => l.updatedAt)) ??
    new Date(0).toISOString();
  const vars = queryVars(
    runsDataQuery,
    singleQuery,
    nBuckets,
    queryType,
    expressions
  );

  // This is a switch statement because Typescript is too dumb to infer the type of the queryBehavior properly, even though
  // everything else is the same.
  // You'd think that this would work:
  //
  // const queryBehaviorsByType = {
  //   BucketedQuery: BucketedQueryBehavior,
  //   BucketedQueryWithInternalId: BucketedQueryWithInternalIdBehavior,
  //   GroupedBucketedQuery: GroupedBucketedQueryBehavior,
  //   GroupedBucketedQueryWithInternalId: GroupedBucketedQueryWithInternalIdBehavior,
  // }
  // const queryBehavior = queryBehaviorsByType[queryType];
  // return await executeQueryBehavior(queryBehavior, vars, runIds, lastUpdated, isShadowQuery, currentRunsById);
  //
  // But it doesn't, even though it's functionally equivalent.
  switch (queryType) {
    case 'BucketedQuery':
      return await executeQueryBehavior(
        BucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        isShadowQuery,
        currentRunsById,
        withoutRunInfo
      );
    case 'BucketedQueryWithInternalId':
      return await executeQueryBehavior(
        BucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        isShadowQuery,
        currentRunsById,
        withoutRunInfo
      );
    case 'GroupedBucketedQuery':
      return await executeQueryBehavior(
        GroupedBucketedQueryBehavior,
        vars,
        runIds,
        lastUpdated,
        isShadowQuery,
        currentRunsById,
        withoutRunInfo
      );
    case 'GroupedBucketedQueryWithInternalId':
      return await executeQueryBehavior(
        GroupedBucketedQueryWithInternalIdBehavior,
        vars,
        runIds,
        lastUpdated,
        isShadowQuery,
        currentRunsById,
        withoutRunInfo
      );
  }
}

function randomShadowLimit() {
  /**
   * We want to compare FF execution speeds across a variety of limits
   * choose one randomly
   */
  const limit = [75, 100, 150, 200, 250];
  return limit[Math.floor(Math.random() * limit.length)];
}

function doGroupedBucketedApiQuery(
  vars: QueryVars,
  currentRuns: string[],
  lastUpdated: string,
  withoutRunInfo: boolean
): GroupedBucketedQueryPromise {
  if (withoutRunInfo) {
    return apolloClientNoCache.query<
      GroupedBucketedRunsDeltaQueryWithoutRunInfoQuery,
      GroupedBucketedRunsDeltaQueryWithoutRunInfoQueryVariables
    >({
      context: propagateErrorsContext(),
      query: GroupedBucketedRunsDeltaQueryWithoutRunInfoDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  } else {
    return apolloClientNoCache.query<
      GroupedBucketedRunsDeltaQueryQuery,
      GroupedBucketedRunsDeltaQueryQueryVariables
    >({
      context: propagateErrorsContext(),
      query: GroupedBucketedRunsDeltaQueryDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  }
}

function doGroupedBucketedApiQueryByInternalId(
  vars: ReturnType<typeof queryVars>,
  currentRuns: string[],
  lastUpdated: string,
  withoutRunInfo: boolean
): GroupedBucketedQueryPromise {
  if (withoutRunInfo) {
    return apolloClientNoCache.query<
      GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
      GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
    >({
      context: propagateErrorsContext(),
      query: GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  } else {
    return apolloClientNoCache.query<
      GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQuery,
      GroupedBucketedRunsDeltaQueryInternalIdWithoutRunInfoQueryVariables
    >({
      context: propagateErrorsContext(),
      query: GroupedBucketedRunsDeltaQueryInternalIdDocument,
      fetchPolicy: 'no-cache',
      variables: {
        ...vars,
        currentRuns,
        lastUpdated,
      },
    });
  }
}

function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedQueryRunsDeltas;
}) {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return currentRunsById;
  } else {
    // new diffOps or deleteOps means we need to refresh the reference for `runsById`
    currentRunsById = Object.assign({}, currentRunsById);
    deleteOps.forEach(rId => {
      delete currentRunsById[rId];
    });
  }

  delta.forEach(runDelta => {
    if (runDelta.__typename !== 'GroupBucketDiff') {
      console.error('Grouped Bucketed Delta Op returned without valid run');
      return;
    }
    if (!runDelta.groupedRun) {
      console.error('Grouped Bucketed Delta Op returned without valid run');
      return;
    }

    try {
      const parsedRun = fromJson(runDelta.groupedRun);
      if (parsedRun == null) {
        console.error('Unable to parse run from server in outliers reducer');
        return;
      }

      // Update the entire record for any diffOp
      currentRunsById[runDelta.groupedRun.id] = {
        ...parsedRun,
        history: runDelta.groupedRun.bucketedHistory,
      };
    } catch (e) {
      console.error('Error parsing run data in outliers reducer', e);
    }
  });
  return currentRunsById;
}
